import { useCallback, useState } from "react";
import { useApi } from "@/hooks/useApi";
import { toast } from "react-toastify";

import type {
  APIResponse,
  OrganizationInvite,
  ResponseMeta,
} from "./types";

// Enhanced invitation types based on your backend models
export interface OrganizationInviteDetail {
  id: string;
  token: string;
  email: string;
  role: string;
  role_display: string;
  status: string;
  status_display: string;
  invited_by: string;
  invited_by_name: string;
  invited_by_email: string;
  organization: string;
  organization_name: string;
  organization_logo_url: string;
  created_at: string;
  updated_at: string;
  expires_at: string;
  days_until_expiry: number;
  accepted_at: string | null;
  accepted_by: string | null;
  accepted_by_name: string | null;
  send_count: number;
  last_sent_at: string | null;
  metadata: Record<string, any> | null;
  is_expired: boolean;
  frontend_url: string;
}

export interface OrganizationInviteList {
  id: string;
  token: string;
  email: string;
  role: string;
  status: string;
  invited_by_name: string;
  organization_name: string;
  created_at: string;
  expires_at: string;
  days_until_expiry: number;
  send_count: number;
  last_sent_at: string | null;
}

// Invite creation request
export interface CreateInviteRequest {
  email: string;
  role: string;
  metadata?: Record<string, any>;
  send_email?: boolean;
}

// Bulk invite request
export interface BulkInviteRequest {
  invitations: Array<{
    email: string;
    role: string;
    metadata?: Record<string, any>;
  }>;
}

// export interface InviteVerificationResponse {
//   organization_name: string;
//   organization_logo: string | null;
//   inviter_name: string;
//   role: string;
//   email: string;
//   expires_at: string;
// }

// export interface AcceptInviteResponse {
//   organization_id: string;
//   organization_name: string;
//   role: string;
// }

// Response types
export type OrganizationInviteListResponse = APIResponse<OrganizationInviteList[]>;
export type OrganizationInviteDetailResponse = APIResponse<OrganizationInviteDetail>;
export type OrganizationInvitePaginationResponse = APIResponse<{
  results: OrganizationInviteList[];
  count: number;
  next: string | null;
  previous: string | null;
}>;
export type InviteVerificationResponse = APIResponse<InviteVerificationResponse>;
export type AcceptInviteResponse = APIResponse<AcceptInviteResponse>;
export type BulkInviteResponse = APIResponse<{
  created_invitations: OrganizationInviteList[];
  failed_invitations: Array<{
    email: string;
    reason: string;
    invitation_id?: string;
  }>;
  total_created: number;
  total_failed: number;
}>;

// The hook for organization invitations
export const useOrganizationInvitation = () => {
  const { request, isLoading, error } = useApi();
  const [invitations, setInvitations] = useState<OrganizationInviteList[]>([]);
  const [currentInvitation, setCurrentInvitation] = useState<OrganizationInviteDetail | null>(null);
  const [paginationMeta, setPaginationMeta] = useState<ResponseMeta | null>(null);

  // Get all invitations for an organization
  const getOrganizationInvitations = useCallback(async (
    organizationId: string,
    page: number = 1,
    status?: string,
    email?: string
  ) => {
    try {
      let url = `/api/organizations/${organizationId}/invitations/?page=${page}`;
      if (status) url += `&status=${status}`;
      if (email) url += `&email=${email}`;

      const response = await request<OrganizationInviteListResponse>(url);
      if (response && response.data) {
        setInvitations(response.data);
        if (response.meta) {
          setPaginationMeta(response.meta);
        }
        return response;
      }
    } catch (error) {
      console.error("Failed to get organization invitations:", error);
      throw error;
    }
  }, [request]);

  // Get a specific invitation
  const getInvitation = useCallback(async (organizationId: string, invitationId: string) => {
    try {
      const response = await request<OrganizationInviteDetailResponse>(
        `/api/organizations/${organizationId}/invitations/${invitationId}/`
      );
      if (response && response.data) {
        setCurrentInvitation(response.data);
        return response;
      }
    } catch (error) {
      console.error("Failed to get invitation details:", error);
      throw error;
    }
  }, [request]);

  // Create a new invitation
  const createInvitation = useCallback(async (
    organizationId: string,
    inviteData: CreateInviteRequest
  ) => {
    try {
      const response = await request<OrganizationInviteDetailResponse>(
        `/api/organizations/${organizationId}/invitations/`,
        {
          method: "POST",
          body: JSON.stringify(inviteData),
        }
      );

      if (response && response.data) {
        toast.success(response.message || "Invitation sent successfully");
        return response;
      }
    } catch (error) {
      console.error("Failed to create invitation:", error);
      toast.error("Failed to send invitation");
      throw error;
    }
  }, [request]);

  // Send bulk invitations
  const bulkInvite = useCallback(async (
    organizationId: string,
    invitationsData: BulkInviteRequest
  ) => {
    try {
      const response = await request<BulkInviteResponse>(
        `/api/organizations/${organizationId}/invitations/bulk_invite/`,
        {
          method: "POST",
          body: JSON.stringify(invitationsData),
        }
      );

      if (response) {
        const successCount = response.data?.total_created || 0;
        const failCount = response.data?.total_failed || 0;

        if (successCount > 0) {
          toast.success(`Successfully sent ${successCount} invitation(s)`);
        }

        if (failCount > 0) {
          toast.warn(`Failed to send ${failCount} invitation(s)`);
        }

        return response;
      }
    } catch (error) {
      console.error("Failed to send bulk invitations:", error);
      toast.error("Failed to send invitations");
      throw error;
    }
  }, [request]);

  // Resend an invitation
  const resendInvitation = useCallback(async (organizationId: string, invitationId: string) => {
    try {
      const response = await request<OrganizationInviteDetailResponse>(
        `/api/organizations/${organizationId}/invitations/${invitationId}/resend/`,
        {
          method: "POST"
        }
      );

      if (response && response.data) {
        toast.success("Invitation resent successfully");
        return response;
      }
    } catch (error) {
      console.error("Failed to resend invitation:", error);
      toast.error("Failed to resend invitation");
      throw error;
    }
  }, [request]);

  // Revoke an invitation
  const revokeInvitation = useCallback(async (organizationId: string, invitationId: string) => {
    try {
      const response = await request<OrganizationInviteDetailResponse>(
        `/api/organizations/${organizationId}/invitations/${invitationId}/revoke/`,
        {
          method: "POST"
        }
      );

      if (response && response.data) {
        toast.success("Invitation revoked successfully");
        return response;
      }
    } catch (error) {
      console.error("Failed to revoke invitation:", error);
      toast.error("Failed to revoke invitation");
      throw error;
    }
  }, [request]);

  // Verify an invitation token (public endpoint)
  const verifyInvitationToken = useCallback(async (token: string) => {
    try {
      const response = await request<InviteVerificationResponse>(
        `/api/invites/${token}/`
      );

      if (response && response.data) {
        return response;
      }
    } catch (error) {
      console.error("Failed to verify invitation:", error);
      throw error;
    }
  }, [request]);

  // Accept an invitation (requires authentication)
  const acceptInvitation = useCallback(async (token: string) => {
    try {
      const response = await request<AcceptInviteResponse>(
        `/api/invites/${token}/accept/`,
        {
          method: "POST"
        }
      );

      if (response && response.data) {
        toast.success("Invitation accepted successfully");
        return response;
      }
    } catch (error) {
      console.error("Failed to accept invitation:", error);
      toast.error("Failed to accept invitation");
      throw error;
    }
  }, [request]);

  const declineInvitation = useCallback(async (token: string) => {
    try {
      const response = await request<APIResponse<{
        organization_id: string;
        organization_name: string;
        email: string;
      }>>(
        `/api/invites/${token}/decline/`,
        {
          method: "POST"
        }
      );

      if (response && response.data) {
        toast.info("Invitation declined");
        return response;
      }
    } catch (error) {
      console.error("Failed to decline invitation:", error);
      toast.error("Failed to decline invitation");
      throw error;
    }
  }, [request]);

  return {
    invitations,
    currentInvitation,
    paginationMeta,
    isLoading,
    error,
    getOrganizationInvitations,
    getInvitation,
    createInvitation,
    bulkInvite,
    resendInvitation,
    revokeInvitation,
    verifyInvitationToken,
    acceptInvitation,
    declineInvitation
  };
};